import clsx from 'clsx';

import {
  FloatingPanel,
  type FloatingPanelProps,
} from '@/shared/ui/FloatingPanel';
import { formatVoiceLength, WAVE_POINTS } from './attachments';

/** Панель записи голосового: красная точка, таймер, ЭКГ с красной заливкой
 *  по прогрессу, «Отмена / Сохранить». Всплывает у якоря — якорь и сторону
 *  задаёт родитель. */
export function RVoicePanel({
  elapsedMs,
  busy,
  onCancel,
  onSave,
  clipId,
  ...placement
}: {
  elapsedMs: number;
  busy?: boolean;
  onCancel: () => void;
  onSave: () => void;
  /** Уникальный id clipPath — на странице панелей может быть несколько. */
  clipId: string;
} & Pick<
  FloatingPanelProps,
  'anchorRef' | 'side' | 'align' | 'sideOffset' | 'topOffset' | 'alignOffset'
>) {
  // Заливка бежит по дорожке и повторяется каждые 12 секунд — как в макете.
  const seconds = Math.floor(elapsedMs / 1000);
  const fill = Math.min(300, (seconds % 12) * 25);

  return (
    <FloatingPanel
      {...placement}
      className={clsx(
        'w-[268px] rounded-[14px] bg-white px-[14px] py-[13px]',
        'shadow-[0_16px_36px_rgba(20,30,49,0.2)]'
      )}
    >
      <div className="flex items-center gap-[8px]">
        <span className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-[#EF4642]" />
        <span className="text-[11px] font-semibold text-[rgba(20,30,49,0.55)]">
          Запись голосового
        </span>
        <span className="flex-1" />
        <span className="text-[12px] font-bold tabular-nums text-[#141E31]">
          {formatVoiceLength(elapsedMs)}
        </span>
      </div>

      <svg
        viewBox="0 0 300 40"
        preserveAspectRatio="none"
        className="mt-[10px] block h-[23px] w-full"
        aria-hidden
      >
        <polyline
          points={WAVE_POINTS}
          fill="none"
          stroke="rgba(46,62,106,0.22)"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points={WAVE_POINTS}
          fill="none"
          stroke="#EF4642"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          clipPath={`url(#${clipId})`}
        />
        <defs>
          <clipPath id={clipId}>
            <rect x="0" y="0" width={fill} height="40" />
          </clipPath>
        </defs>
      </svg>

      <div className="mt-[12px] flex items-center justify-end gap-[14px]">
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] font-semibold text-[rgba(20,30,49,0.55)]"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className={clsx(
            'border-b border-[#57B792] pb-[3px] pt-[2px] text-[12px] font-bold text-[#57B792]',
            busy && 'cursor-not-allowed opacity-50'
          )}
        >
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </FloatingPanel>
  );
}
